package com.elan.edubridge.ui.member

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.ClassData
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.repository.EduConnectRepository
import com.elan.edubridge.shared.domain.usecase.ObserveMyTimelineUseCase
import com.elan.edubridge.ui.shell.KST_OFFSET_MS
import com.elan.edubridge.ui.shell.accountName
import kotlin.js.Date

/**
 * 회원 뷰 페이지 7종이 공통으로 필요로 하는 부트스트랩과 날짜·일정 계산을 모은 파일이다.
 * `ui/shell/ShellSupport.kt`와 같은 성격 — 여러 페이지가 반복하는 최소 유틸리티만 둔다.
 */

internal class MemberContext(
    val session: SessionState,
    val repository: EduConnectRepository,
    val timelineUseCase: ObserveMyTimelineUseCase,
)

internal suspend fun loadMemberContext(session: SessionState): MemberContext {
    val repository = SharedGraph.createRepository()
    return MemberContext(
        session = session,
        repository = repository,
        timelineUseCase = SharedGraph.createObserveMyTimelineUseCase(),
    )
}

/** 본인 활성 멤버십의 학원. 없으면 null(§4.2 "학원 없음"). */
internal fun MemberContext.subjectAcademyId(): String? = session.activeMembership?.academyId

/** 알림함처럼 학원 id 목록이 필요한 조회용 — 본인 활성 멤버십의 학원 id(없으면 빈 목록). */
internal fun MemberContext.subjectAcademyIds(): List<String> =
    listOfNotNull(session.activeMembership?.academyId)

/** 본인 uid. */
internal fun MemberContext.subjectStudentUserId(): String? = session.account?.id

internal fun MemberContext.subjectDisplayName(): String = session.accountName()

/** (academyId, studentUserId) 쌍 — 알림·과제 집계 등 반복 조회해야 하는 곳에서 쓴다. */
internal fun MemberContext.subjectAssignmentTargets(): List<Pair<String, String>> {
    val academyId = session.activeMembership?.academyId
    val uid = session.account?.id
    return if (academyId != null && uid != null) listOf(academyId to uid) else emptyList()
}

/* ---------- 날짜·시각 ---------- */

/** 학원 기준 KST의 오늘(§4-9 결정). 브라우저 로컬을 쓰면 해외 체류 사용자의 "오늘 출결"이
 * 현지 날짜로 조회돼 어긋난다 — serviceDate 키는 학원의 달력이 기준이다. */
internal fun todayServiceDate(): String {
    val view = Date(Date().getTime() + KST_OFFSET_MS)
    val month = (view.getUTCMonth() + 1).toString().padStart(2, '0')
    val day = view.getUTCDate().toString().padStart(2, '0')
    return "${view.getUTCFullYear()}-$month-$day"
}

internal fun formatServiceDate(date: Date): String {
    val year = date.getFullYear()
    val month = (date.getMonth() + 1).toString().padStart(2, '0')
    val day = date.getDate().toString().padStart(2, '0')
    return "$year-$month-$day"
}

// 시:분 표기는 `ui/shell/ShellSupport.kt`의 `formatKstClockTime`이 유일본이다(§7.1 타임라인 스텝
// 정본 `a h:mm`). 이전의 `formatClockTime`(오전/오후 없는 "6:10" — 등·하원에서 결함급)과
// `formatKoreanTime`(브라우저 로컬)은 그 유일본으로 통합돼 삭제됐다.

private val ISO_DAY_NAMES = listOf("월", "화", "수", "목", "금", "토", "일")

/**
 * 오늘 이후 가장 가까운 수업 1건과 표시 라벨("오늘 19:00" 등)을 계산한다.
 * 오늘 이미 시작 시각이 지났으면 다음 주기(최대 7일 뒤)로 넘어간다.
 * `studentSchedule()`가 돌려준 목록 안에서만 계산하는 순수 함수라 [MyTodayPage]·[MySchedulePage]
 * 양쪽이 같은 로직을 반복하지 않도록 여기 하나만 둔다.
 */
internal fun nextClassOccurrence(classes: List<ClassData>): Pair<ClassData, String>? {
    val now = Date()
    val nowIsoDay = if (now.getDay() == 0) 7 else now.getDay()
    val nowMinute = now.getHours() * 60 + now.getMinutes()

    var bestClass: ClassData? = null
    var bestDaysAhead = Int.MAX_VALUE
    var bestMinute = Int.MAX_VALUE

    for (classData in classes) {
        for (day in classData.schedule.daysOfWeek) {
            var daysAhead = (day - nowIsoDay + 7) % 7
            if (daysAhead == 0 && classData.schedule.startMinuteOfDay < nowMinute) daysAhead = 7
            val startMinute = classData.schedule.startMinuteOfDay
            if (daysAhead < bestDaysAhead || (daysAhead == bestDaysAhead && startMinute < bestMinute)) {
                bestClass = classData
                bestDaysAhead = daysAhead
                bestMinute = startMinute
            }
        }
    }

    val found = bestClass ?: return null
    val label = when (bestDaysAhead) {
        0 -> "오늘"
        1 -> "내일"
        else -> "${ISO_DAY_NAMES[(nowIsoDay - 1 + bestDaysAhead) % 7]}요일"
    }
    return found to "$label ${found.schedule.startLabel}"
}
